'use client';

import type { FC } from 'react';
import { useRef, useCallback } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, EmptyState } from '../../components/ui';
import { GiOpenChest } from '../../components/icons';
import { CaseCard } from '../../components/cases/CaseCard';
import { CaseCardSkeleton } from '../../components/CaseCardSkeleton';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCasesQuery } from '../../queries/useCasesQuery';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import translations from './CasesScreen.translations';

export const CasesScreen: FC = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: cases = [], isLoading, error, refetch } = useCasesQuery();
  const isDesktop = useIsDesktop();
  const showSkeleton = isLoading && cases.length === 0;

  const handleCaseClick = useCallback(
    (caseId: string) => {
      navigation.navigate('case-details', { caseId });
    },
    [navigation],
  );

  if (showSkeleton) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenTabBar />
        <ScreenContent scrollRef={scrollRef} scrollKey="cases">
          <div className={`grid pt-4 ${isDesktop ? 'grid-cols-4 gap-3' : 'grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] gap-1'}`} style={{ paddingBottom: 'calc(72px + 16px + var(--tg-viewport-safe-area-inset-bottom, 0px))' }}>
            {[...Array(6)].map((_, index) => (
              <CaseCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        </ScreenContent>
      </>
    );
  }

  if (error && cases.length === 0) {
    return (
      <>
        <ScreenFixedTop title={t('title')} />
        <ScreenTabBar />
        <ScreenContent>
          <ErrorMessage onRetry={() => refetch()} />
        </ScreenContent>
      </>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent scrollRef={scrollRef} scrollKey="cases">
        {cases.length === 0 ? (
          <EmptyState
            icon={<GiOpenChest size={48} className="text-white/20" />}
            title={t('empty')}
            description={t('emptyHint')}
            action={
              <Button size="l" onClick={() => navigation.navigate('games')}>
                {t('emptyCta')}
              </Button>
            }
          />
        ) : (
          <div className={`grid pt-4 ${isDesktop ? 'grid-cols-4 gap-3' : 'grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] gap-1'}`} style={{ paddingBottom: 'calc(72px + 16px + var(--tg-viewport-safe-area-inset-bottom, 0px))' }}>
            {cases.map((caseItem) => (
              <CaseCard
                key={caseItem.id}
                id={caseItem.id}
                name={caseItem.name}
                imageUrl={caseItem.imageUrl}
                imagePlaceholder={caseItem.imagePlaceholder}
                priceInCredits={caseItem.priceInCredits}
                openingsCount={caseItem.openingsCount}
                isNew={caseItem.isNew}
                onClick={handleCaseClick}
              />
            ))}
          </div>
        )}
      </ScreenContent>
    </>
  );
};
